import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Tag,
  Space,
  Select,
  message,
  Typography,
  Row,
  Col,
  Statistic,
  Badge,
  Tooltip,
  Popconfirm,
} from 'antd';
import {
  EyeOutlined,
  MailOutlined,
  CheckCircleOutlined,
  QuestionCircleOutlined,
  CloseCircleOutlined,
  RobotOutlined,
  BarChartOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { Reply, ReplyStats, Campaign } from '../../types/outreach';
import { outreachApi } from '../../services/outreach';
import { ROUTES } from '../../config/routes';
import { COLORS } from '../../constants/colors';

const { Title, Text } = Typography;
const { Option } = Select;

// 意向标签配置（4种新枚举: positive / neutral / negative / follow_up）
const sentimentConfig: Record<string, { color: string; text: string; icon: React.ReactNode }> = {
  positive: { color: 'success', text: '积极', icon: <CheckCircleOutlined /> },
  neutral: { color: 'default', text: '中性', icon: <RobotOutlined /> },
  negative: { color: 'error', text: '消极', icon: <CloseCircleOutlined /> },
  follow_up: { color: 'warning', text: '待跟进', icon: <QuestionCircleOutlined /> },
};

export const Replies: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Reply[]>([]);
  const [total, setTotal] = useState(0);
  const [pagination, setPagination] = useState({ page: 1, page_size: 10 });
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [selectedCampaign, setSelectedCampaign] = useState<string>('');
  const [sentimentFilter, setSentimentFilter] = useState<string>('');
  const [stats, setStats] = useState<ReplyStats | null>(null);
  const [analyzing, setAnalyzing] = useState<Set<string>>(new Set());

  // 获取活动列表
  useEffect(() => {
    fetchCampaigns();
  }, []);

  // 获取回复列表
  useEffect(() => {
    if (selectedCampaign) {
      fetchReplies();
      fetchStats();
    }
  }, [selectedCampaign, pagination, sentimentFilter]);

  const fetchCampaigns = async () => {
    try {
      const result = await outreachApi.getCampaigns({ page: 1, page_size: 100 });
      if (result.data) {
        setCampaigns(result.data.list || []);
        // 默认选择第一个活动
        if (result.data.list && result.data.list.length > 0 && !selectedCampaign) {
          setSelectedCampaign(result.data.list[0].id);
        }
      }
    } catch (error) {
      console.error('获取活动列表失败:', error);
    }
  };

  const fetchReplies = async () => {
    if (!selectedCampaign) return;
    
    setLoading(true);
    try {
      const result = await outreachApi.listReplies(selectedCampaign, {
        page: pagination.page,
        page_size: pagination.page_size,
        sentiment: sentimentFilter,
      });
      if (result.data) {
        setData(result.data.list || []);
        setTotal(result.data.meta?.total || 0);
      }
    } catch (error) {
      console.error('获取回复列表失败:', error);
      message.error('获取回复列表失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    if (!selectedCampaign) return;
    
    try {
      const result = await outreachApi.getReplyStats(selectedCampaign);
      if (result.data) {
        setStats(result.data);
      }
    } catch (error) {
      console.error('获取回复统计失败:', error);
    }
  };

  const handleAnalyze = async (replyId: string) => {
    setAnalyzing(prev => new Set(prev).add(replyId));
    try {
      await outreachApi.analyzeReply(replyId);
      message.success('分析已启动，请稍后刷新查看结果');
    } catch (error) {
      console.error('分析失败:', error);
      message.error('分析失败');
    } finally {
      setAnalyzing(prev => {
        const next = new Set(prev);
        next.delete(replyId);
        return next;
      });
    }
  };

  const handleMarkAsRead = async (replyId: string) => {
    try {
      await outreachApi.markReplyRead(replyId);
      message.success('已标记为已读');
      fetchReplies();
    } catch (error) {
      console.error('标记已读失败:', error);
      message.error('标记已读失败');
    }
  };

  const getSentimentTag = (reply: Reply) => {
    const sentiment = reply.ai_analysis?.sentiment || reply.intent_classification || 'unknown';
    const config = sentimentConfig[sentiment] || { color: 'default', text: '未分析', icon: null };
    
    return (
      <Tag color={config.color} icon={config.icon}>
        {config.text}
      </Tag>
    );
  };

  const getConfidenceBadge = (reply: Reply) => {
    const confidence = reply.ai_analysis?.confidence;
    if (!confidence) return null;
    
    let color = 'default';
    if (confidence >= 0.8) color = 'success';
    else if (confidence >= 0.6) color = 'warning';
    else color = 'error';
    
    return (
      <Badge 
        count={`${Math.round(confidence * 100)}%`} 
        style={{ backgroundColor: color === 'success' ? COLORS.success : color === 'warning' ? COLORS.warning : COLORS.error }}
      />
    );
  };

  const columns = [
    {
      title: '发件人',
      key: 'sender',
      width: 200,
      render: (_: unknown, record: Reply) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.from_name || '未知'}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>{record.from_email || '-'}</Text>
        </div>
      ),
    },
    {
      title: '主题',
      dataIndex: 'subject',
      key: 'subject',
      ellipsis: true,
      render: (subject: string, record: Reply) => (
        <Space>
          {!record.is_read && <Badge status="processing" />}
          <Text>{subject || '无主题'}</Text>
        </Space>
      ),
    },
    {
      title: '内容摘要',
      key: 'content',
      ellipsis: true,
      render: (_: unknown, record: Reply) => (
        <Text type="secondary" style={{ fontSize: 13 }}>
          {record.reply_content?.substring(0, 100)}...
        </Text>
      ),
    },
    {
      title: '意向分析',
      key: 'sentiment',
      width: 150,
      render: (_: unknown, record: Reply) => (
        <Space>
          {getSentimentTag(record)}
          {getConfidenceBadge(record)}
        </Space>
      ),
    },
    {
      title: '接收时间',
      dataIndex: 'received_at',
      key: 'received_at',
      width: 160,
      render: (time: string) => (time ? new Date(time).toLocaleString('zh-CN') : '-'),
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      render: (_: unknown, record: Reply) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => navigate(`${ROUTES.OUTREACH_REPLIES}/${record.id}`)}
          >
            查看
          </Button>
          {!record.ai_analysis?.sentiment && (
            <Button
              type="link"
              size="small"
              icon={<ReloadOutlined />}
              loading={analyzing.has(record.id)}
              onClick={() => handleAnalyze(record.id)}
            >
              分析
            </Button>
          )}
          {!record.is_read && (
            <Button
              type="link"
              size="small"
              onClick={() => handleMarkAsRead(record.id)}
            >
              标记已读
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24 }}>
        <Title level={4}>
          <MailOutlined style={{ marginRight: 8 }} />
          回复监测
        </Title>
      </div>

      {/* 统计卡片 */}
      {stats && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={4}>
            <Card>
              <Statistic
                title="总回复"
                value={stats.total_replies}
                prefix={<MailOutlined />}
              />
            </Card>
          </Col>
          <Col span={4}>
            <Card>
              <Statistic
                title="正面意向"
                value={stats.positive_count}
                valueStyle={{ color: COLORS.success }}
                prefix={<CheckCircleOutlined />}
              />
            </Card>
          </Col>
          <Col span={4}>
            <Card>
              <Statistic
                title="中性"
                value={stats.neutral_count}
                prefix={<RobotOutlined />}
              />
            </Card>
          </Col>
          <Col span={4}>
            <Card>
              <Statistic
                title="消极"
                value={stats.negative_count}
                valueStyle={{ color: COLORS.error }}
                prefix={<CloseCircleOutlined />}
              />
            </Card>
          </Col>
          <Col span={4}>
            <Card>
              <Statistic
                title="待跟进"
                value={stats.follow_up_count}
                valueStyle={{ color: COLORS.warning }}
                prefix={<QuestionCircleOutlined />}
              />
            </Card>
          </Col>
          <Col span={4}>
            <Card>
              <Statistic
                title="回复率"
                value={stats.reply_rate?.toFixed(1)}
                suffix="%"
                prefix={<BarChartOutlined />}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* 筛选栏 */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={16} align="middle">
          <Col span={8}>
            <Space>
              <Text>活动:</Text>
              <Select
                style={{ width: 280 }}
                placeholder="选择活动"
                value={selectedCampaign}
                onChange={setSelectedCampaign}
              >
                {campaigns.map(campaign => (
                  <Option key={campaign.id} value={campaign.id}>
                    {campaign.name}
                  </Option>
                ))}
              </Select>
            </Space>
          </Col>
          <Col span={8}>
            <Space>
              <Text>意向筛选:</Text>
              <Select
                style={{ width: 150 }}
                placeholder="全部"
                allowClear
                value={sentimentFilter}
                onChange={setSentimentFilter}
              >
                <Option value="positive">积极</Option>
                <Option value="neutral">中性</Option>
                <Option value="negative">消极</Option>
                <Option value="follow_up">待跟进</Option>
              </Select>
            </Space>
          </Col>
          <Col span={8} style={{ textAlign: 'right' }}>
            <Button icon={<ReloadOutlined />} onClick={fetchReplies}>
              刷新
            </Button>
          </Col>
        </Row>
      </Card>

      {/* 回复列表 */}
      <Card>
        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.page,
            pageSize: pagination.page_size,
            total: total,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条`,
            onChange: (page, pageSize) => setPagination({ page, page_size: pageSize || 10 }),
          }}
          locale={{ emptyText: selectedCampaign ? '暂无回复数据' : '请选择活动' }}
          onRow={(record) => ({
            onClick: () => navigate(`${ROUTES.OUTREACH_REPLIES}/${record.id}`),
            style: { cursor: 'pointer' },
          })}
        />
      </Card>
    </div>
  );
};
